/**
 * Where a pricing visit came from — the missing link in the conversion funnel.
 *
 * Why this exists (#1220, CRO, 2026-08-04): the three funnel steps already
 * fired — `fit_viewed`, `upgrade_viewed`, `checkout_started` — but nothing
 * connected them. `upgrade_viewed` fired with `{}`, so "did reading a fit
 * analysis lead anyone to pricing, and on to checkout?" was unanswerable even
 * though every event in the chain existed. The CRO's issue read this as "no
 * such events exist"; the truth was narrower and more fixable.
 *
 * A CTA links to /pricing?from=<surface>. PricingPage records it here, and
 * checkout reads it back, so all three steps carry the same origin.
 *
 * NOT named `source`: GA4 treats `source`/`medium`/`campaign` as traffic
 * ATTRIBUTION inputs, and sending one as an event param rewrites the session's
 * real acquisition channel — the bug fixed the same day in the analytics
 * wrapper. `from_surface` is deliberately outside that reserved set.
 *
 * sessionStorage, not module state: Stripe checkout is a full navigation away
 * and back, and a tab reload must not silently invent an origin. Every access
 * is guarded — private-mode Safari throws on sessionStorage.
 */

const KEY = 'stratia_funnel_origin';

// Only origins we deliberately instrument. An unknown value is dropped rather
// than recorded: a funnel dimension that accepts arbitrary strings stops being
// groupable the first time someone hand-edits the URL.
// 'fit_read' is the retired bottom card (kept so a session that began there
// still records); 'fit_read_verdict' is its replacement line (#2427).
export const FUNNEL_ORIGINS = ['fit_read', 'fit_read_verdict', 'major_map', 'roadmap', 'nav', 'welcome'];

export function setFunnelOrigin(origin) {
    if (!FUNNEL_ORIGINS.includes(origin)) return null;
    try { window.sessionStorage.setItem(KEY, origin); } catch { /* private mode */ }
    return origin;
}

/** The recorded origin, or null. Never throws, never guesses. */
export function getFunnelOrigin() {
    try {
        const v = window.sessionStorage.getItem(KEY);
        return FUNNEL_ORIGINS.includes(v) ? v : null;
    } catch {
        return null;
    }
}

export function clearFunnelOrigin() {
    try { window.sessionStorage.removeItem(KEY); } catch { /* private mode */ }
}

/**
 * Read `?from=` off a pricing visit and record it. Returns the origin, or null
 * when the visitor arrived some other way — null is an honest "direct", not a
 * reason to fabricate one.
 */
export function captureFunnelOriginFromUrl(search = window.location.search) {
    try {
        const from = new URLSearchParams(search).get('from');
        return from ? setFunnelOrigin(from) : getFunnelOrigin();
    } catch {
        return null;
    }
}
